/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ErrorCode } from '../../modules/order-details';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';
import { escapeODataStringLiteral } from '../../utilities/odata';

/**
 * GetSoldTos Input Action
 */
export class GetSoldTosInput implements Msdyn365.IActionInput {
    public timestamp: string;
    constructor() {
        this.timestamp = new Date().getTime().toString();
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `GetSoldTos-${this.timestamp}`;
    public getCacheObjectType = () => 'GetSoldTos';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetSoldTosData {
    result: {
        '@odata.context'?: string;
        value?: ISoldToTableData[];
    };
    error?: ErrorCode;
}

export interface ISoldToTableData {
    Id: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    SalesAreas: ISalesArea[];
    LocalAddress: ILocalAddress;
    ExtensionProperties: [];
}

export interface ILocalAddress {
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    ExtensionProperties: [];
}

export interface ISalesArea {
    SoldToId: string;
    ShipToId: string;
    Language: string;
    SalesOrg: string;
    SalesOrgText: string;
    DistributionChannel: string;
    DistributionChannelText: string;
    Division: string;
    DivisionText: string;
    OrderCreate: boolean;
    Requester: string;
    RecordCreatedDate: Date;
    ExtensionProperties: [];
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetSoldTosInput();
};

async function action(_: GetSoldTosInput, ctx: Msdyn365.IActionContext): Promise<any> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IGetSoldTosData['result']>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetSoldTos(language='${escapeODataStringLiteral(ctx.requestContext.locale)}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );

    if (response.status === 200 && response.data) {
        const validSoldtos = response.data?.value?.filter(
            soldto => soldto.SalesAreas.length > 0 && soldto.SalesAreas.some(area => !!area.OrderCreate)
        );

        return {
            ...response.data,
            value: validSoldtos
        };
    } else {
        return { result: { value: [] }, error: { errorCode: response.status, errorText: response.statusText } };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetSoldTosData>>((input, ctx) => trackedAction(action, input, ctx, 'GetSoldTos')),
    id: 'GetSoldTos',
    input: createInput
});
